import styled from '@emotion/styled';
import {Hidden, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import Link from 'next/link';

import {transient} from 'Application/configs/emotionTransientProps';
import type {IconType} from 'Images/icons';

/**
 * The `ComponentProps` interface defines the properties for the `TopBarLink` component.
 * This interface establishes the contract for rendering a styled navigation link with an icon and optional label in the top bar.
 * The component supports various navigation scenarios including external links and provides accessibility features through proper testing identifiers.
 */
interface ComponentProps {
    /**
     * The URL or path that the link should navigate to when clicked.
     * This can be a relative path within the application or an absolute URL for external navigation.
     * The href value is passed directly to the Next.js Link component for optimal routing performance.
     */
    href: string;
    /**
     * The icon component to display alongside the link text.
     * This should be a valid IconType component that will be rendered with consistent dimensions and styling.
     * The icon provides visual context and improves the user experience by making navigation elements more recognizable.
     */
    icon: IconType;
    /**
     * The text label to display next to the icon in the navigation link.
     * This label provides textual context for the navigation action and improves accessibility for screen readers.
     * The label is displayed using the theme's primary text color and Montserrat font family.
     */
    label: string;
    /**
     * The optional target attribute for the link element.
     * Common values include '_blank' for opening in a new window or '_self' for the current window.
     * When not provided, the link will use the default browser behavior for navigation.
     */
    target?: string;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'TopBarLink'
     */
    testId?: string;
}

/**
 * The `TopBarLink` component renders a styled link with an icon and optional label for use in the top navigation bar.
 * This component wraps a Next.js Link component with custom styling and provides consistent visual appearance across the application.
 * The component uses MobX observer pattern to enable reactive updates when used with observable data stores.
 *
 * @param props        The component props.
 * @param props.href   The URL or path that the link should navigate to when clicked.
 * @param props.icon   The icon component to display alongside the link text.
 * @param props.label  The optional text label to display next to the icon.
 * @param props.target The optional target attribute for the link (e.g., '_blank' for new window).
 * @param props.testId The unique identifier for testing purposes.
 * @returns The TopBarLink component JSX element.
 *
 * @example
 * ```tsx
 * <TopBarLink
 *     href="/dashboard"
 *     icon={HomeIcon}
 *     label="Dashboard"
 *     testId="dashboard-link"
 * />
 * ```
 */
const TopBarLink = observer(({href, icon, label, target, testId = 'TopBarLink'}: ComponentProps) => {
    const Icon = icon;

    return (
        <Wrapper data-cy={testId} href={href} target={target}>
            <Icon height={21} width={21} />
            <Hidden isLoadingHtml sm xs>
                <Span>{label}</Span>
            </Hidden>
        </Wrapper>
    );
});

TopBarLink.displayName = 'TopBarLink';

export {TopBarLink};

const Wrapper = styled(Link)`
    align-items: center;
    border-radius: 4px;
    color: ${({theme}) => theme.colors.textPrimary};
    display: flex;
    font-family: ${({theme}) => theme.fonts.Montserrat};
    font-size: 1.2rem;
    font-weight: 500;
    gap: ${spacing(6)};
    outline: 0px solid transparent;
    text-decoration: underline hsla(from ${({theme}) => theme.colors.textPrimary} H S L / 0);
    transition: outline 0.2s ease-in-out, text-decoration 0.2s ease-in-out;

    &:hover {
        text-decoration: underline hsla(from ${({theme}) => theme.colors.textPrimary} H S L / 1);
    }
    &:focus-visible {
        outline: 2px solid ${({theme}) => theme.colors.secondaryDark};
        outline-offset: 2px;
        text-decoration: underline hsla(from ${({theme}) => theme.colors.textPrimary} H S L / 1);
    }
`;

const Span = styled('span', transient)``;